import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '../api';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastContext';
import PhotoEditor from '../components/PhotoEditor';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { T } from '../styles/theme';

const AI_LOADING_TEXTS = ['✍️ Analizzo il prodotto...', '🔍 Cerco le parole chiave migliori...', '📝 Scrivo il titolo ottimizzato...', '💰 Calcolo il prezzo ideale...', '🏷️ Genero gli hashtag perfetti...', '✅ Quasi pronto...'];

// ─── Helper: esporta foto modificate dal PhotoEditor ──────
const preparaFotoPerVinted = async (fotoUrls) => {
  if (!fotoUrls || fotoUrls.length === 0) return [];
  const risultati = [];
  for (const url of fotoUrls.slice(0, 20)) {
    try {
      let blob;
      if (url.startsWith('data:')) {
        const r = await fetch(url);
        blob = await r.blob();
      } else {
        const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
        const r = await fetch(fullUrl);
        blob = await r.blob();
      }
      const base64 = await new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 400;
          let w = img.width, h = img.height;
          if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
          else { w = Math.round(w * maxSize / h); h = maxSize; }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.5));
        };
        img.onerror = () => resolve(null);
        img.src = URL.createObjectURL(blob);
      });
      if (base64) risultati.push(base64);
    } catch (e) { console.warn('[Foto] Errore:', e); }
  }
  return risultati;
};

// ─── Helper: toast celebrativo ────────────────────────────
const showSuccessToast = (titolo, account) => {
  document.getElementById('success-toast-overlay')?.remove();
  const container = document.createElement('div');
  container.id = 'success-toast-overlay';
  container.style.cssText = 'position:fixed;inset:0;z-index:999999;display:flex;align-items:center;justify-content:center;background:rgba(13,11,30,0.7);';
  container.innerHTML = `
    <div style="background:#151230;border:1px solid #7c3aed;border-radius:24px;padding:40px 48px;text-align:center;box-shadow:0 0 60px rgba(124,58,237,0.5);font-family:DM Sans,sans-serif;min-width:340px;max-width:420px;position:relative;z-index:1000000;filter:none !important;backdrop-filter:none !important;">
      <div style="font-size:3.5rem;margin-bottom:16px;">🎉</div>
      <div style="font-family:Syne,sans-serif;font-size:1.5rem;font-weight:800;color:#e2e8f0;margin-bottom:10px;">Pubblicato con successo!</div>
      <div style="font-size:0.9rem;color:#a78bfa;margin-bottom:6px;font-weight:600;">${titolo || 'Il tuo annuncio'}</div>
      ${account ? `<div style="font-size:0.82rem;color:#a78bfa;margin-bottom:6px;">pubblicato come @${account}</div>` : ''}
      <div style="font-size:0.82rem;color:#6b7280;margin-bottom:24px;">è ora visibile su Vinted 🛍️</div>
      <div style="height:4px;border-radius:2px;background:linear-gradient(90deg,#7c3aed,#4f46e5,#a78bfa);animation:shrinkBar 3s linear forwards;"></div>
    </div>
    <style>@keyframes shrinkBar{from{width:100%}to{width:0%}}</style>
  `;
  document.body.appendChild(container);
  container.addEventListener('click', (e) => { if (e.target === container) container.remove(); });
  setTimeout(() => { if (container.parentNode) { container.style.opacity = '0'; container.style.transition = 'opacity 0.3s'; setTimeout(() => container.remove(), 300); } }, 3000);
};

export default function CreaAnnuncio() {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [aiLoadingText, setAiLoadingText] = useState(0);
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [piattaforma, setPiattaforma] = useState('entrambi');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [editEbay, setEditEbay] = useState({ titolo: '', descrizione: '', prezzo: 0 });
  const [editVinted, setEditVinted] = useState({ titolo: '', descrizione: '', prezzo: 0, tags: '' });
  const [publishing, setPublishing] = useState(false);
  const [ebayStatus, setEbayStatus] = useState(null);
  const [showVintedModal, setShowVintedModal] = useState(false);
  const [showVintedInstructions, setShowVintedInstructions] = useState(false);
  const [vintedSending, setVintedSending] = useState(false);
  const [vintedReadyUrl, setVintedReadyUrl] = useState('');
  const [showVintedLink, setShowVintedLink] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [fotoModificateCrea, setFotoModificateCrea] = useState([]);
  const [bozzaId, setBozzaId] = useState(null);
  const [waitingVintedConfirm, setWaitingVintedConfirm] = useState(false);
  const [vintedPublishData, setVintedPublishData] = useState(null);
  const [confirmingVinted, setConfirmingVinted] = useState(false);
  const [showEbaySuccess, setShowEbaySuccess] = useState(null); // {titolo, link}
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  // Listener: quando l'utente torna sulla dashboard dopo aver aperto Vinted
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && waitingVintedConfirm) {
        // L'utente è tornato — il popup è già visibile
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [waitingVintedConfirm]);

  // Non bloccare se estensione non rilevata — procedi comunque
  const vintedInstalled = true;

  // ─── Account Vinted ────────────────────────────────────
  const [accountList, setAccountList] = useState([]);
  const [accountSelezionato, setAccountSelezionato] = useState(null);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);

  useEffect(() => {
    api.get('/products?per_page=200').then((r) => {
      const items = r.data.items || [];
      setProducts(items);
      if (location.state?.prodotto) {
        const p = location.state.prodotto;
        const found = items.find((i) => i.id === p.id);
        if (found) {
          setSelectedProduct(found); setStep(3);
        } else {
          // Prodotto non in lista — caricalo direttamente
          api.get(`/products/${p.id}`).then((r2) => {
            if (r2.data) { setSelectedProduct(r2.data); setStep(3); }
          }).catch(() => {});
        }
      }
    }).catch(() => {});
    api.get('/auth/ebay-status').then((r) => setEbayStatus(r.data)).catch(() => {});
    api.get('/account-piattaforme').then((r) => {
      const data = Array.isArray(r.data) ? r.data : [];
      setAccountList(data);
    }).catch(() => setAccountList([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!generating) return;
    const interval = setInterval(() => setAiLoadingText((p) => (p + 1) % AI_LOADING_TEXTS.length), 2500);
    return () => clearInterval(interval);
  }, [generating]);

  const vintedAccounts = accountList.filter(a => a.piattaforma === 'Vinted' || a.piattaforma === 'vinted');
  const selAccount = vintedAccounts.find(a => a.id === accountSelezionato);

  // ─── Foto del prodotto ─────────────────────────────────
  const prodottoFotoUrls = selectedProduct
    ? ((selectedProduct.photos || []).map(p => p.foto_url).length > 0
        ? (selectedProduct.photos || []).map(p => p.foto_url)
        : (selectedProduct.foto_url ? [selectedProduct.foto_url] : []))
    : [];

  const handleGenerate = async () => {
    if (!selectedProduct) return;
    setGenerating(true);
    try {
      const res = await api.post('/annunci/genera', { product_id: selectedProduct.id, piattaforma });
      setResult(res.data);
      setEditEbay({ titolo: res.data.titolo_ebay || '', descrizione: res.data.descrizione_ebay || '', prezzo: res.data.prezzo_suggerito || selectedProduct.price });
      setEditVinted({ titolo: res.data.titolo_vinted || '', descrizione: res.data.descrizione_vinted || '', prezzo: res.data.prezzo_suggerito || selectedProduct.price, tags: (res.data.tags_vinted || []).join(', ') });
      setStep(4);
      toast.success('Annuncio generato!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione');
    } finally { setGenerating(false); }
  };

  const handlePublishEbay = async () => {
    if (!ebayStatus?.collegato) { toast.error('Collega prima eBay dal profilo'); return; }
    setPublishing(true);
    try {
      const bozze = await api.get('/annunci/bozze');
      const ebayBozza = bozze.data.find(b => b.product_id === selectedProduct.id && b.piattaforma === 'ebay' && b.stato === 'bozza');
      if (!ebayBozza) { toast.error('Bozza non trovata'); setPublishing(false); return; }
      await api.put(`/annunci/bozze/${ebayBozza.id}`, { titolo: editEbay.titolo, descrizione: editEbay.descrizione, prezzo: editEbay.prezzo });
      const fd = new FormData();
      fd.append('bozza_id', ebayBozza.id);
      const res = await api.post('/annunci/pubblica-ebay', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      if (res.data.success) {
        setShowEbaySuccess({
          titolo: editEbay.titolo,
          link: res.data.link || res.data.url || '',
          itemId: res.data.item_id || '',
        });
      } else toast.error(res.data.error || 'Errore pubblicazione');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore pubblicazione'); }
    finally { setPublishing(false); }
  };

  const handlePublishVinted = async () => {
    setVintedSending(true);
    // Apri la tab SUBITO (sincrono) per evitare il popup blocker di Chrome
    const newTab = window.open('about:blank', '_blank');
    if (!newTab) {
      setVintedSending(false);
      toast.error("Chrome ha bloccato l'apertura della tab. Autorizza i popup per dashboard.proelite.it nelle impostazioni del browser.");
      return;
    }
    try {
      newTab.document.write('<html><head><title>Apro Vinted...</title></head><body style="font-family:system-ui;background:#0c0a1a;color:#a78bfa;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;"><div style="text-align:center;"><h2>Preparazione dati...</h2><p>Tra qualche secondo verrai reindirizzato a Vinted</p></div></body></html>');
    } catch (e) { /* ignora se about:blank non permette document.write */ }
    try {
      // Comprimi foto via proxy (max 5, 300px, qualità 0.4)
      const haModificate = fotoModificateCrea.some(Boolean);
      const fotoFinali = haModificate
        ? prodottoFotoUrls.map((orig, i) => fotoModificateCrea[i] || orig)
        : prodottoFotoUrls;

      let fotoCompresse = [];
      for (const url of fotoFinali.slice(0, 20)) {
        try {
          let blob;
          if (url.startsWith('data:')) {
            const r = await fetch(url); blob = await r.blob();
          } else {
            const path = url.startsWith('/') ? url.replace('/uploads/', '/media/') : `/media/${url}`;
            const proxyUrl = `${API_BASE}/proxy-image?url=${encodeURIComponent(path)}`;
            const r = await fetch(proxyUrl); blob = await r.blob();
          }
          const b64 = await new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
              const c = document.createElement('canvas');
              const max = 800;
              let w = img.width, h = img.height;
              if (w > h) { h = Math.round(h * max / w); w = max; } else { w = Math.round(w * max / h); h = max; }
              c.width = w; c.height = h;
              c.getContext('2d').drawImage(img, 0, 0, w, h);
              resolve(c.toDataURL('image/jpeg', 0.75));
            };
            img.onerror = () => resolve(null);
            img.src = URL.createObjectURL(blob);
          });
          if (b64) fotoCompresse.push(b64);
        } catch (e) { console.warn('[Foto] Errore:', e); }
      }

      const vintedData = {
        titolo: editVinted.titolo || '',
        descrizione: (editVinted.descrizione || '').replace(/<[^>]*>/g, '').trim(),
        prezzo: parseFloat(editVinted.prezzo || 0),
        categoria: selectedProduct?.category || '',
        marca: selectedProduct?.brand || '',
        taglia: selectedProduct?.taglia || '',
        colore: selectedProduct?.colore || '',
        condizioni: selectedProduct?.condition || 'Buone',
        fotoBase64: fotoCompresse,
        accountSelezionato: selAccount?.username || '',
      };

      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(vintedData))));
      const vintedUrl = `https://www.vinted.it/items/new#IE_DRAFT=${encoded}`;
      console.log('[CreaAnnuncio] Apro Vinted, URL length:', vintedUrl.length, 'foto:', fotoCompresse.length);

      // Salva i dati per la conferma al ritorno
      setVintedPublishData({
        titolo: editVinted.titolo,
        descrizione: editVinted.descrizione,
        prezzo: editVinted.prezzo,
        fotoUrl: prodottoFotoUrls[0] || '',
        account: selAccount?.username || selAccount?.nome_account || '',
        prodottoId: selectedProduct?.id,
      });

      if (newTab && !newTab.closed) {
        newTab.location.href = vintedUrl;
        toast.success('Apro Vinted con i dati...');
        setWaitingVintedConfirm(true);
      } else {
        toast.error('La tab è stata chiusa prima di completare la preparazione');
      }
    } catch (err) {
      if (newTab && !newTab.closed) newTab.close();
      toast.error('Errore preparazione dati');
      console.error('[CreaAnnuncio] Errore:', err);
    } finally { setVintedSending(false); }
  };

  const handleDownloadZip = async () => {
    if (!selectedProduct) return;
    setDownloadingZip(true);
    try {
      const zip = new JSZip();
      const fotoUrl = prodottoFotoUrls;
      for (let i = 0; i < fotoUrl.length; i++) {
        const resp = await fetch(`${API_BASE}${fotoUrl[i]}`);
        const blob = await resp.blob();
        const ext = fotoUrl[i].split('.').pop() || 'jpg';
        zip.file(`foto_${i + 1}.${ext}`, blob);
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const safeName = selectedProduct.name.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      saveAs(content, `foto_${safeName}.zip`);
      toast.success('Foto scaricate!');
    } catch { toast.error('Errore download foto'); }
    finally { setDownloadingZip(false); }
  };

  const handleSaveDraft = async () => {
    try {
      const titolo = piattaforma === 'vinted' ? editVinted.titolo : editEbay.titolo;
      const descrizione = piattaforma === 'vinted' ? editVinted.descrizione : editEbay.descrizione;
      const prezzo = piattaforma === 'vinted' ? editVinted.prezzo : editEbay.prezzo;
      const fotoUrls = prodottoFotoUrls;

      // Esporta foto modificate (merge con originali)
      const hasMod = fotoModificateCrea.some(Boolean);
      let fotoModificateBase64 = [];
      if (hasMod) {
        const merged = fotoUrls.map((orig, i) => fotoModificateCrea[i] || orig);
        fotoModificateBase64 = await preparaFotoPerVinted(merged);
      }

      const res = await api.post('/bozze', {
        titolo,
        descrizione,
        prezzo: parseFloat(prezzo) || 0,
        categoria: selectedProduct?.category || '',
        hashtag: editVinted.tags ? editVinted.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        piattaforma: piattaforma === 'entrambi' ? 'entrambe' : piattaforma,
        foto_urls: fotoUrls,
        foto_modificate_urls: fotoModificateBase64.length > 0 ? fotoModificateBase64 : [],
        prodotto_id: selectedProduct?.id || null,
        note: '',
      });
      setBozzaId(res.data?.id || null);
      toast.success('Bozza salvata!');
      setTimeout(() => navigate('/bozze'), 1500);
    } catch { toast.error('Errore salvataggio bozza'); }
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.info('Copiato!'); };

  return (
    <Layout>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => window.history.back()} style={{
          background: T.elem, border: `1px solid ${T.border}`, borderRadius: 24, padding: '10px 20px',
          color: T.text, fontFamily: T.fontBody, fontSize: '0.9rem', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', transition: '0.15s', marginBottom: 16,
        }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.violet; e.currentTarget.style.background = T.hover; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.elem; }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.violet} strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Torna indietro
        </button>

        <h1 className="page-title">Crea Annuncio</h1>
        <p className="section-subtitle">Genera annunci ottimizzati con IA</p>

        <div className="flex gap-2 mb-8">
          {['Prodotto', 'Piattaforma', 'Genera', 'Pubblica'].map((s, i) => (
            <div key={i} className="flex-1 h-1.5 rounded-full" style={{ background: step > i ? T.gradPrimary : T.faint }} />
          ))}
        </div>

        {step === 1 && (
          <div>
            <h2 className="section-title" style={{ fontFamily: T.fontDisplay, color: T.text }}>Scegli un prodotto</h2>
            {products.length === 0 ? (
              <div className="card p-12 text-center"><p style={{ color: T.muted, fontFamily: T.fontBody }}>Aggiungi prima un prodotto all'inventario</p></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? 8 : 16 }}>
                {products.map((p) => (
                  <button key={p.id} onClick={() => { setSelectedProduct(p); setStep(2); }}
                    className="rounded-xl border overflow-hidden text-left transition-all hover:scale-[1.02]"
                    style={{
                      backgroundColor: T.card,
                      borderColor: selectedProduct?.id === p.id ? T.borderActive : T.border,
                      boxShadow: selectedProduct?.id === p.id ? `0 0 0 2px ${T.borderActive}30` : 'none',
                    }}>
                    <div className="aspect-square" style={{ backgroundColor: T.bg, height: isMobile ? 100 : 'auto' }}>
                      {p.foto_url ? <img src={`${API_BASE}${p.foto_url}`} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-3xl opacity-30">📦</div>}
                    </div>
                    <div style={{ padding: isMobile ? 8 : 12 }}>
                      <p className="text-sm font-medium" style={{ color: T.text, fontFamily: T.fontBody, fontSize: isMobile ? '0.75rem' : '0.9rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal' }}>{p.name}</p>
                      <p className="text-xs" style={{ color: T.muted, fontFamily: T.fontBody, fontSize: isMobile ? '0.65rem' : '0.78rem' }}>{p.category} {p.condition && `· ${p.condition}`}</p>
                      <p className="text-sm font-bold mt-1" style={{ color: T.violet, fontSize: isMobile ? '0.85rem' : '1rem' }}>{'\u20AC'}{p.price.toFixed(2)}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="section-title" style={{ fontFamily: T.fontDisplay, color: T.text }}>Dove vuoi pubblicare?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[{ v: 'ebay', icon: '🏷️', label: 'Solo eBay', desc: 'Annuncio professionale con SEO' },
                { v: 'vinted', icon: '🛍️', label: 'Solo Vinted', desc: 'Testo naturale e accattivante' },
                { v: 'entrambi', icon: '🚀', label: 'Entrambi', desc: 'Massima visibilita' },
              ].map((opt) => (
                <button key={opt.v} onClick={() => setPiattaforma(opt.v)}
                  className="p-5 rounded-2xl border text-left transition-all"
                  style={{
                    borderColor: piattaforma === opt.v ? T.borderActive : T.border,
                    backgroundColor: piattaforma === opt.v ? T.borderActive + '08' : 'transparent',
                  }}>
                  <span className="text-2xl">{opt.icon}</span>
                  <p className="font-bold mt-2" style={{ color: T.text, fontFamily: T.fontBody }}>{opt.label}</p>
                  <p className="text-sm mt-1" style={{ color: T.muted, fontFamily: T.fontBody }}>{opt.desc}</p>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="btn-ghost text-sm">← Indietro</button>
              <button onClick={() => setStep(3)} className="btn-primary text-sm">Avanti →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center">
            {!generating ? (
              <div className="card p-12">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: T.borderActive + '15' }}><span className="text-3xl">✨</span></div>
                <h2 className="font-bold text-xl mb-2" style={{ color: T.text, fontFamily: T.fontDisplay }}>Pronto per generare</h2>
                <p className="mb-2" style={{ color: T.muted, fontFamily: T.fontBody }}>{selectedProduct?.name} → {piattaforma === 'entrambi' ? 'eBay + Vinted' : piattaforma}</p>
                <button onClick={handleGenerate} className="btn-primary px-8 py-3 rounded-xl mt-4">✨ Genera Annuncio con IA</button>
                <div className="mt-4"><button onClick={() => setStep(2)} className="text-sm hover:opacity-80" style={{ color: T.muted }}>← Indietro</button></div>
              </div>
            ) : (
              <div className="rounded-2xl p-12" style={{ backgroundColor: T.card, border: `1px solid ${T.border}`, borderRadius: 16 }}>
                <div className="relative w-[100px] h-[100px] mx-auto mb-6">
                  <div className="w-[100px] h-[100px] rounded-full animate-spin" style={{ background: `conic-gradient(${T.violetDark}, ${T.violetDeep}, ${T.violet}, ${T.violetDark})`, animationDuration: '2s' }} />
                  <div className="absolute inset-2 rounded-full flex items-center justify-center" style={{ backgroundColor: T.card }}>
                    <span className="text-3xl">🤖</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 mb-6 max-w-xs mx-auto">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-0.5 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${T.violet}, transparent)`, animation: `scanline 1.5s ease-in-out ${i * 0.3}s infinite` }} />
                  ))}
                </div>
                <p className="font-bold text-lg mb-2" style={{ color: T.text, fontFamily: T.fontBody }}>🤖 Sto creando il tuo annuncio...</p>
                <p className="text-sm mb-4 transition-opacity" style={{ color: T.violet }}>{AI_LOADING_TEXTS[aiLoadingText]}</p>
                <div className="w-64 h-2 rounded-full mx-auto mb-4 overflow-hidden" style={{ backgroundColor: T.bg, borderRadius: 8 }}>
                  <div className="h-full rounded-full" style={{ background: T.gradPrimary, animation: 'progressBar 18s linear forwards', animationFillMode: 'forwards' }} />
                </div>
                <div className="flex gap-2 justify-center">
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: T.violet, animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 4 && result && (
          <div>
            {/* Miniature foto cliccabili + bottone editor */}
            {prodottoFotoUrls.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 8, overflowX: 'auto', flex: 1 }}>
                    {prodottoFotoUrls.map((url, idx) => {
                      const fullUrl = url.startsWith('http') || url.startsWith('data:') ? url : `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
                      const isEdited = fotoModificateCrea[idx];
                      return (
                        <div key={idx} style={{ position: 'relative', flexShrink: 0, cursor: 'pointer' }}
                          onClick={() => {
                            const editorEl = document.getElementById('crea-photo-editor');
                            if (editorEl) editorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}>
                          <img src={isEdited || fullUrl} alt={`Foto ${idx + 1}`} style={{
                            width: 72, height: 72, objectFit: 'cover', borderRadius: 12,
                            border: isEdited ? `2px solid ${T.green}` : `2px solid ${T.border}`,
                            transition: 'all 0.2s',
                          }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = T.violet}
                            onMouseLeave={e => { if (!isEdited) e.currentTarget.style.borderColor = T.border; }}
                          />
                          {isEdited && (
                            <span style={{
                              position: 'absolute', top: -4, right: -4, background: T.green,
                              color: '#fff', fontSize: '0.55rem', padding: '1px 5px', borderRadius: 8, fontWeight: 700,
                            }}>Editata</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => {
                      const editorEl = document.getElementById('crea-photo-editor');
                      if (editorEl) editorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    style={{
                      padding: '8px 16px', borderRadius: 12, border: `1px solid ${T.violetDark}`,
                      background: 'rgba(124,58,237,0.1)', color: T.violet,
                      fontSize: '0.82rem', fontWeight: 600, fontFamily: T.fontBody,
                      cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >
                    📷 Modifica foto
                  </button>
                </div>
              </div>
            )}

            <div className={`grid ${piattaforma === 'entrambi' ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 max-w-2xl mx-auto'} gap-6`}>
              {(piattaforma === 'ebay' || piattaforma === 'entrambi') && (
                <div className="card p-6">
                  <div className="flex items-center gap-2 mb-4"><span className="badge badge-cyan" style={{ fontFamily: T.fontBody }}>eBay</span><span className="text-xs" style={{ color: T.muted }}>{editEbay.titolo.length}/80</span></div>
                  <div className="space-y-4">
                    <div><label className="label">Titolo</label><input value={editEbay.titolo} onChange={(e) => setEditEbay({...editEbay, titolo: e.target.value.slice(0,80)})} className="input" /></div>
                    <div><label className="label">Descrizione</label><textarea value={editEbay.descrizione} onChange={(e) => setEditEbay({...editEbay, descrizione: e.target.value})} rows={6} className="input" /></div>
                    <div><label className="label">Prezzo ({'\u20AC'})</label><input type="number" step="0.01" value={editEbay.prezzo} onChange={(e) => setEditEbay({...editEbay, prezzo: parseFloat(e.target.value) || 0})} className="input" /></div>
                  </div>
                </div>
              )}
              {(piattaforma === 'vinted' || piattaforma === 'entrambi') && (
                <div className="card p-6">
                  <div className="flex items-center gap-2 mb-4"><span className="badge badge-green" style={{ fontFamily: T.fontBody }}>Vinted</span><span className="text-xs" style={{ color: T.muted }}>{editVinted.titolo.length}/60</span></div>
                  <div className="space-y-4">
                    <div><label className="label">Titolo</label><input value={editVinted.titolo} onChange={(e) => setEditVinted({...editVinted, titolo: e.target.value.slice(0,60)})} className="input" /></div>
                    <div><label className="label">Descrizione</label><textarea value={editVinted.descrizione} onChange={(e) => setEditVinted({...editVinted, descrizione: e.target.value})} rows={6} className="input" /></div>
                    <div><label className="label">Prezzo ({'\u20AC'})</label><input type="number" step="0.01" value={editVinted.prezzo} onChange={(e) => setEditVinted({...editVinted, prezzo: parseFloat(e.target.value) || 0})} className="input" /></div>
                    <div><label className="label">Tags</label><input value={editVinted.tags} onChange={(e) => setEditVinted({...editVinted, tags: e.target.value})} className="input" placeholder="tag1, tag2, tag3" /></div>
                  </div>
                </div>
              )}
            </div>

            {/* Editor foto */}
            {prodottoFotoUrls.length > 0 && (
              <div className="mt-6" id="crea-photo-editor">
                <PhotoEditor
                  fotoUrls={prodottoFotoUrls}
                  onFotoModificate={(edited) => setFotoModificateCrea(edited)}
                />
              </div>
            )}

            {/* Account Vinted dropdown */}
            {(piattaforma === 'vinted' || piattaforma === 'entrambi') && (
              <div style={{ marginTop: 20, maxWidth: 420 }}>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: T.fontBody, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Account Vinted
                </p>
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <div onClick={() => setAccountDropdownOpen(!accountDropdownOpen)} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: '#1c1640', border: `1px solid ${accountSelezionato ? '#7c3aed' : '#2d1b69'}`,
                    borderRadius: 14, padding: '12px 16px', cursor: 'pointer',
                  }}>
                    {selAccount ? (
                      <>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: selAccount.colore || '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 700, fontFamily: T.fontBody, flexShrink: 0 }}>
                          {(selAccount.nome_account || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ color: T.text, fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody }}>{selAccount.nome_account}</span>
                          {selAccount.username && <span style={{ color: T.muted, fontSize: '0.72rem', marginLeft: 8, fontFamily: T.fontBody }}>@{selAccount.username}</span>}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: T.muted, fontSize: '0.85rem', fontFamily: T.fontBody, flex: 1 }}>Seleziona account Vinted...</span>
                    )}
                    <span style={{ color: '#a78bfa', fontSize: '0.7rem', transition: 'transform 0.2s', transform: accountDropdownOpen ? 'rotate(180deg)' : 'rotate(0)', flexShrink: 0 }}>▼</span>
                  </div>

                  {accountDropdownOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                      background: '#151230', border: '1px solid #2d1b69', borderRadius: 14,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 100,
                      maxHeight: 200, overflowY: 'auto',
                    }}>
                      {vintedAccounts.map((a) => (
                        <div key={a.id} onClick={() => { setAccountSelezionato(a.id); setAccountDropdownOpen(false); }} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 14px', cursor: 'pointer',
                          background: accountSelezionato === a.id ? '#1c1640' : 'transparent',
                          transition: 'background 0.15s',
                        }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: a.colore || '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 700, fontFamily: T.fontBody, flexShrink: 0 }}>
                            {(a.nome_account || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <span style={{ color: T.text, fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody }}>{a.nome_account}</span>
                            {a.username && <span style={{ color: T.muted, fontSize: '0.72rem', marginLeft: 8, fontFamily: T.fontBody }}>@{a.username}</span>}
                          </div>
                          {accountSelezionato === a.id && <span style={{ color: '#7c3aed', fontSize: '1rem' }}>✓</span>}
                        </div>
                      ))}
                      <div onClick={() => { setAccountDropdownOpen(false); navigate('/bozze'); }} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '10px 14px', cursor: 'pointer',
                        borderTop: vintedAccounts.length > 0 ? '1px solid #2d1b69' : 'none',
                      }}>
                        <span style={{ color: '#a78bfa', fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody }}>+ Aggiungi account</span>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid #2d1b69', borderRadius: 10, padding: '10px 14px' }}>
                  <p style={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: T.fontBody, margin: 0 }}>
                    💡 Assicurati di essere loggato con questo account su Vinted prima di pubblicare
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={handleSaveDraft} className="btn-ghost text-sm">💾 Salva Bozze</button>
              {(piattaforma === 'ebay' || piattaforma === 'entrambi') && (
                <button onClick={handlePublishEbay} disabled={publishing} className="btn-primary text-sm disabled:opacity-50">
                  {publishing ? 'Pubblicazione...' : '🚀 Pubblica su eBay'}
                </button>
              )}
              {(piattaforma === 'vinted' || piattaforma === 'entrambi') && (
                <button onClick={handlePublishVinted} disabled={vintedSending} className="btn-secondary text-sm disabled:opacity-50">
                  {vintedSending ? 'Preparazione...' : '🛍️ Pubblica su Vinted'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Vinted Link Ready — clicca per aprire */}
        {showVintedLink && (
          <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{ background: 'rgba(13,11,30,0.92)' }}>
            <div className="max-w-lg w-full shadow-2xl" style={{ backgroundColor: '#151230', border: '1px solid #2d1f6e', borderRadius: 20, padding: 28, textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: 12 }}>🛍️</div>
              <p style={{ color: '#6b7280', fontSize: '0.88rem', marginBottom: 20, fontFamily: 'DM Sans' }}>
                I dati sono pronti. Clicca il bottone per aprire Vinted.
              </p>
              <a
                href={vintedReadyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { setShowVintedLink(false); setShowVintedInstructions(true); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  background: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
                  color: 'white', padding: '16px 32px', borderRadius: 9999,
                  fontFamily: 'DM Sans', fontWeight: 700, fontSize: '1rem',
                  textDecoration: 'none', boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
                }}
              >
                🛍️ Apri Vinted e pubblica
              </a>
              <button onClick={() => setShowVintedLink(false)} style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: '#6b7280', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans' }}>
                Annulla
              </button>
            </div>
          </div>
        )}

        {/* Vinted Instructions Modal — dopo apertura tab */}
        {showVintedInstructions && (
          <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{ background: 'rgba(13,11,30,0.92)' }}>
            <div className="max-w-lg w-full shadow-2xl" style={{ backgroundColor: '#151230', border: '1px solid #2d1f6e', borderRadius: 20, padding: 28 }}>
              <h4 style={{ fontFamily: T.fontDisplay, fontSize: '1.2rem', color: T.text, margin: '0 0 16px' }}>🚀 Vinted si è aperto!</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {[
                  { icon: '✅', text: 'Vinted è aperto nel browser' },
                  { icon: '✅', text: 'I dati sono pronti per l\'estensione' },
                  { icon: '⏳', text: 'L\'estensione sta compilando i campi...' },
                  { icon: '⏳', text: 'Controlla e clicca Pubblica su Vinted' },
                ].map((s, i) => (
                  <div key={i} style={{ background: '#1c1640', borderRadius: 12, padding: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '1.1rem' }}>{s.icon}</span>
                    <span style={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text }}>{s.text}</span>
                  </div>
                ))}
              </div>
              {selAccount && (
                <div style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid #2d1b69', borderRadius: 12, padding: 12, marginBottom: 12 }}>
                  <p style={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: '#a78bfa', margin: 0 }}>
                    👤 Pubblicando come <strong>@{selAccount.username || selAccount.nome_account}</strong>
                  </p>
                </div>
              )}
              <div style={{ background: 'rgba(124,58,237,0.1)', border: `1px solid ${T.violetDark}`, borderRadius: 12, padding: 12, marginBottom: 16 }}>
                <p style={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.violet, margin: 0 }}>
                  💡 Assicurati che l'estensione Impero Elite sia attiva su Chrome
                </p>
              </div>
              <button onClick={async () => {
                // Aggiorna bozza se esiste
                if (bozzaId) {
                  try {
                    await api.put(`/bozze/${bozzaId}`, {
                      stato: 'pubblicata',
                      account_pubblicato: selAccount?.nome_account || '',
                      username_pubblicato: selAccount?.username || '',
                    });
                  } catch {}
                }
                setShowVintedInstructions(false);
                showSuccessToast(editVinted.titolo, selAccount?.username);
                setTimeout(() => navigate('/bozze'), 3000);
              }} className="btn-primary" style={{ width: '100%', padding: 14, borderRadius: 14, justifyContent: 'center', fontSize: '0.95rem' }}>
                ✓ Ho pubblicato
              </button>
            </div>
          </div>
        )}

        {/* Vinted Modal — fallback manuale */}
        {showVintedModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{ background: 'rgba(13,11,30,0.92)' }}>
            <div className="max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: T.card, border: `1px solid ${T.border}`, borderRadius: 20, padding: 24 }}>
              <h3 className="font-bold text-lg mb-4" style={{ color: T.cyan, fontFamily: T.fontDisplay }}>Prepara per Vinted</h3>

              {!vintedInstalled ? (
                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: T.amber + '10', border: `1px solid ${T.amber}30` }}>
                  <p className="text-sm font-medium mb-1" style={{ color: T.amber, fontFamily: T.fontBody }}>Installa l'estensione Chrome</p>
                  <p className="text-xs mb-3" style={{ color: T.muted, fontFamily: T.fontBody }}>Per pubblicare automaticamente su Vinted.</p>
                  <a href="/profilo" className="btn-primary inline-block py-2 px-5 text-sm">
                    Installa dal Profilo →
                  </a>
                </div>
              ) : (
                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: T.bg }}>
                  <p className="text-sm font-medium mb-2" style={{ color: T.text, fontFamily: T.fontBody }}>Copia manualmente se l'estensione non funziona:</p>
                </div>
              )}

              <div className="space-y-3 mb-4">
                <div className="rounded-xl p-3" style={{ backgroundColor: T.bg }}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs" style={{ color: T.muted }}>Titolo</p>
                    <button onClick={() => copyText(editVinted.titolo)} className="text-xs hover:underline" style={{ color: T.violet }}>Copia</button>
                  </div>
                  <p className="text-sm" style={{ color: T.text, fontFamily: T.fontBody }}>{editVinted.titolo}</p>
                </div>
                <div className="rounded-xl p-3" style={{ backgroundColor: T.bg }}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs" style={{ color: T.muted }}>Descrizione</p>
                    <button onClick={() => copyText(editVinted.descrizione)} className="text-xs hover:underline" style={{ color: T.violet }}>Copia</button>
                  </div>
                  <p className="text-sm whitespace-pre-wrap line-clamp-6" style={{ color: T.text, fontFamily: T.fontBody }}>{editVinted.descrizione}</p>
                </div>
                <div className="rounded-xl p-3" style={{ backgroundColor: T.bg }}>
                  <p className="text-xs mb-1" style={{ color: T.muted }}>Prezzo</p>
                  <p className="font-bold" style={{ color: T.violet }}>{'\u20AC'}{editVinted.prezzo.toFixed(2)}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <a href="https://www.vinted.it/items/new" target="_blank" rel="noopener noreferrer"
                  className="btn-primary flex-1 py-2.5 text-center text-sm">
                  Apri Vinted
                </a>
                <button onClick={handleDownloadZip} disabled={downloadingZip} className="btn-secondary py-2.5 px-5 text-sm disabled:opacity-50">
                  {downloadingZip ? 'Download...' : '📦 Scarica foto (.zip)'}
                </button>
                <button onClick={() => setShowVintedModal(false)} className="btn-ghost text-sm">Chiudi</button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Popup conferma Vinted — appare quando l'utente torna dalla tab Vinted ─── */}
        {waitingVintedConfirm && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(13,11,30,0.88)', padding: 16,
          }}>
            <div style={{
              background: T.card, border: `2px solid ${T.violetDark}`, borderRadius: 24,
              padding: isMobile ? '32px 20px' : '40px 48px', textAlign: 'center',
              boxShadow: '0 0 80px rgba(124,58,237,0.4), 0 0 200px rgba(124,58,237,0.15)',
              maxWidth: 440, width: '100%', fontFamily: T.fontBody,
            }}>
              <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>🛍️</div>
              <h3 style={{
                fontFamily: T.fontDisplay, fontSize: '1.4rem', fontWeight: 800,
                color: T.text, marginBottom: 8,
              }}>
                Hai pubblicato su Vinted?
              </h3>
              <p style={{ color: T.muted, fontSize: '0.88rem', marginBottom: 8 }}>
                Conferma per tracciare l'annuncio nella sezione Annunci
              </p>
              {vintedPublishData?.titolo && (
                <p style={{
                  color: T.violet, fontSize: '0.9rem', fontWeight: 600,
                  marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {vintedPublishData.titolo}
                </p>
              )}
              {vintedPublishData?.account && (
                <p style={{ color: T.muted, fontSize: '0.82rem', marginBottom: 20 }}>
                  come @{vintedPublishData.account}
                </p>
              )}
              {!vintedPublishData?.account && <div style={{ marginBottom: 20 }} />}

              <button
                onClick={async () => {
                  setConfirmingVinted(true);
                  try {
                    // Traccia l'annuncio nella sezione Annunci Vinted
                    const fotoUrl = vintedPublishData?.fotoUrl || '';
                    const fullFotoUrl = fotoUrl.startsWith('http') ? fotoUrl : (fotoUrl ? `${API_BASE}${fotoUrl}` : '');
                    await api.post('/annunci/vinted/log', {
                      titolo: vintedPublishData?.titolo || '',
                      prezzo: parseFloat(vintedPublishData?.prezzo || 0),
                      foto_url: fullFotoUrl,
                      descrizione: vintedPublishData?.descrizione || '',
                      url_annuncio: '',
                      prodotto_id: vintedPublishData?.prodottoId || null,
                    });
                    // Aggiorna bozza se esiste
                    if (bozzaId) {
                      try {
                        await api.put(`/bozze/${bozzaId}`, {
                          stato: 'pubblicata',
                          account_pubblicato: selAccount?.nome_account || '',
                          username_pubblicato: selAccount?.username || '',
                        });
                      } catch {}
                    }
                    setWaitingVintedConfirm(false);
                    setVintedPublishData(null);
                    showSuccessToast(vintedPublishData?.titolo, vintedPublishData?.account);
                    setTimeout(() => navigate('/annunci'), 3000);
                  } catch {
                    toast.error('Errore nel tracciamento');
                  } finally { setConfirmingVinted(false); }
                }}
                disabled={confirmingVinted}
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 14, border: 'none',
                  background: T.gradPrimary, color: '#fff', fontSize: '1rem',
                  fontWeight: 700, fontFamily: T.fontBody, cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
                  opacity: confirmingVinted ? 0.7 : 1,
                  marginBottom: 10,
                }}
              >
                {confirmingVinted ? 'Conferma in corso...' : '✓ Si, ho pubblicato!'}
              </button>
              <button
                onClick={() => { setWaitingVintedConfirm(false); setVintedPublishData(null); }}
                style={{
                  width: '100%', padding: '10px 0', borderRadius: 12, border: `1px solid ${T.border}`,
                  background: 'transparent', color: T.muted, fontSize: '0.85rem',
                  fontFamily: T.fontBody, cursor: 'pointer',
                }}
              >
                No, non ancora
              </button>
            </div>
          </div>
        )}

        {/* ─── Popup successo eBay ─── */}
        {showEbaySuccess && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(13,11,30,0.88)', padding: 16,
          }}>
            <div style={{
              background: T.card, border: `2px solid ${T.green}`, borderRadius: 24,
              padding: isMobile ? '32px 20px' : '40px 48px', textAlign: 'center',
              boxShadow: '0 0 80px rgba(74,222,128,0.3), 0 0 200px rgba(74,222,128,0.1)',
              maxWidth: 440, width: '100%', fontFamily: T.fontBody,
            }}>
              <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>🎉</div>
              <h3 style={{
                fontFamily: T.fontDisplay, fontSize: '1.4rem', fontWeight: 800,
                color: T.text, marginBottom: 8,
              }}>
                Pubblicato su eBay!
              </h3>
              <p style={{
                color: T.violet, fontSize: '0.9rem', fontWeight: 600,
                marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {showEbaySuccess.titolo}
              </p>
              <p style={{ color: T.green, fontSize: '0.85rem', marginBottom: 24 }}>
                Il tuo annuncio e ora visibile su eBay
              </p>

              <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
                {showEbaySuccess.link && (
                  <a
                    href={showEbaySuccess.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: '100%', padding: '14px 0', borderRadius: 14, border: 'none',
                      background: T.gradPrimary, color: '#fff', fontSize: '0.95rem',
                      fontWeight: 700, fontFamily: T.fontBody, cursor: 'pointer',
                      textDecoration: 'none', display: 'block', textAlign: 'center',
                      boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
                    }}
                  >
                    Vedi su eBay
                  </a>
                )}
                <button
                  onClick={() => { setShowEbaySuccess(null); navigate('/annunci'); }}
                  style={{
                    width: '100%', padding: '12px 0', borderRadius: 12,
                    border: `1px solid ${T.border}`, background: 'transparent',
                    color: T.text, fontSize: '0.88rem', fontWeight: 600,
                    fontFamily: T.fontBody, cursor: 'pointer',
                  }}
                >
                  Vai ai tuoi Annunci
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </Layout>
  );
}
